import React from 'react';
import { Map, Compass, Anchor, Navigation, Plane, ShieldCheck } from 'lucide-react';
import { motion } from 'motion/react';
import { useLanguage } from '../context/LanguageContext';

export function ServiceAreas() {
  const { lang, t } = useLanguage();

  const regions = [
    {
      id: 'bassin',
      title: t('seo_local_title_1'),
      subtitle: t('seo_local_sub_1'),
      description: t('seo_local_desc_1'),
      icon: Anchor,
      cities: lang === 'fr' ? [
        "Cap Ferret (L'Herbe, Canon)",
        "Arcachon & Pyla-sur-Mer",
        "Le Teich (Studio principal)",
        "La Teste-de-Buch",
        "Gujan-Mestras & ses ports",
        "Andernos-les-Bains",
        "Lanton & les plages sauvages",
        "Arès & Réserve naturelle"
      ] : [
        "Cap Ferret (L'Herbe, Canon)",
        "Arcachon & Pyla-sur-Mer",
        "Le Teich (Main Studio)",
        "La Teste-de-Buch",
        "Gujan-Mestras & its oyster ports",
        "Andernos-les-Bains",
        "Lanton & wild beaches",
        "Arès & Nature Reserve"
      ]
    },
    {
      id: 'bordeaux',
      title: t('seo_local_title_2'),
      subtitle: t('seo_local_sub_2'),
      description: t('seo_local_desc_2'),
      icon: Compass,
      cities: lang === 'fr' ? [
        "Bordeaux (Centre historique)",
        "Pessac-Léognan & Pessac",
        "Saint-Émilion & Pomerol",
        "Le Médoc & ses châteaux",
        "Sauternes & Graves",
        "Châteaux du Périgord (Dordogne)",
        "Les Landes & Côte Basque"
      ] : [
        "Bordeaux (Historic Center)",
        "Pessac-Léognan & Pessac",
        "Saint-Émilion & Pomerol",
        "Le Médoc & its castles",
        "Sauternes & Graves",
        "Perigord Châteaux (Dordogne)",
        "Les Landes & Basque Coast"
      ]
    },
    {
      id: 'mobility',
      title: t('seo_local_title_3'),
      subtitle: t('seo_local_sub_3'),
      description: t('seo_local_desc_3'),
      icon: Plane,
      cities: lang === 'fr' ? [
        "Déplacements Nationaux (Paris, Provence)",
        "Destination Weddings (Europe)",
        "Prises de vue par Drone autorisées",
        "Suivi logistique clé en main"
      ] : [
        "National Travels (Paris, Provence)",
        "Destination Weddings (Europe)",
        "Licensed Drone Filming",
        "Turnkey Logistics Support"
      ]
    }
  ];

  return (
    <section id="localisation" className="py-24 bg-theme-bg border-t border-theme-border relative overflow-hidden z-10">
      {/* Subtle organic background decoration */}
      <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[500px] h-[500px] bg-theme-accent/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative">
        
        {/* Header section with high SEO semantic weight */}
        <div className="max-w-3xl mb-16">
          <span className="text-xs uppercase tracking-[0.3em] text-theme-accent font-mono block mb-3">
            {t('seo_local_tag')}
          </span>
          <h2 className="text-3xl md:text-5xl font-mono text-theme-text font-extrabold tracking-tight mb-6">
            {t('seo_local_title')}
          </h2>
          <div className="w-16 h-[2px] bg-theme-accent mb-6" />
          <p 
            className="text-sm md:text-base text-theme-secondary font-light leading-relaxed"
            dangerouslySetInnerHTML={{ __html: t('seo_local_desc') }}
          />
        </div>

        {/* Bento Grid layout for geographic areas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {regions.map((region) => {
            const IconComponent = region.icon;
            return (
              <div 
                key={region.id}
                className="bg-theme-card border border-theme-border p-8 flex flex-col justify-between hover:border-theme-border-hover transition-all duration-300 relative group"
              >
                {/* Visual marker inside the card */}
                <div className="absolute top-6 right-8 opacity-10 group-hover:opacity-20 transition-opacity text-theme-accent">
                  <IconComponent className="w-12 h-12" />
                </div>

                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-theme-bg border border-theme-border text-theme-accent">
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-[9px] font-mono uppercase tracking-widest text-theme-secondary">{region.subtitle}</p>
                      <h3 className="text-lg font-bold font-mono text-theme-text">{region.title}</h3>
                    </div>
                  </div>

                  <p className="text-xs text-theme-secondary/90 leading-relaxed mb-6 font-light">
                    {region.description}
                  </p>
                </div>

                {/* City checklist optimized for organic search crawlers */}
                <div className="border-t border-theme-border/60 pt-6">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-theme-accent mb-3 flex items-center gap-1.5">
                    <Navigation className="w-3 h-3 animate-pulse" />
                    <span>{t('seo_local_sectors')}</span>
                  </p>
                  <ul className="grid grid-cols-1 gap-2">
                    {region.cities.map((city, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-xs text-theme-secondary/85 hover:text-theme-text transition-colors">
                        <span className="w-1 h-1 rounded-full bg-theme-accent" />
                        <span>{city}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        {/* Dynamic SEO Guarantee Ribbon */}
        <div className="mt-12 p-6 bg-theme-card border border-theme-border flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4 text-left">
            <div className="p-3 bg-theme-bg border border-theme-accent/20 text-theme-accent rounded-full flex-shrink-0">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <div>
              <h4 className="text-sm font-mono uppercase tracking-wider font-bold text-theme-text">{t('seo_local_guarantee_title')}</h4>
              <p className="text-xs text-theme-secondary font-light">{t('seo_local_guarantee_desc')}</p>
            </div>
          </div>
          <a 
            href="#contact" 
            className="px-5 py-2.5 bg-theme-bg hover:bg-theme-accent border border-theme-border hover:border-theme-accent text-theme-text hover:text-theme-bg text-xs font-mono uppercase tracking-wider transition-all duration-300 whitespace-nowrap"
          >
            {t('seo_local_guarantee_cta')}
          </a>
        </div>

      </div>
    </section>
  );
}

